// ABOUTME: Admin goal creation modal for creating goals for children in a family from admin interface
// ABOUTME: Identical UX to user GoalsManager but uses admin API endpoints and bypasses family permissions

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
  Fade,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  GoalType,
  CreateGoalRequest,
} from '../types/api';
import { goalsAPI } from '../services/api';

// Interface compatible with AdminFamilyDetail's FamilyChild
interface FamilyChild {
  id: number;
  firstName: string;
  lastName: string;
  birthDate: string;
  joinedAt: string;
  avatarUrl?: string;
}

interface AdminGoalCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  familyId: number;
  children: FamilyChild[];
  showSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const AdminGoalCreationModal: React.FC<AdminGoalCreationModalProps> = ({
  open,
  onClose,
  onSave,
  familyId,
  children,
  showSnackbar,
}) => {
  const [selectedChildIds, setSelectedChildIds] = useState<number[]>([]);
  const [newGoal, setNewGoal] = useState<CreateGoalRequest>({
    childId: 0,
    title: '',
    type: GoalType.HeartStreak,
    targetValue: 5,
    reward: '',
    dayPasses: 0,
    heartStreakTemplate: {
      consecutiveDays: 5,
      dayPasses: 1
    },
    starCollectionTemplate: {
      totalStars: 10,
      timePeriodDays: 0  // Default to unlimited time (no limit)
    },
    heartCollectionTemplate: {
      totalHearts: 10,
      timePeriodDays: 0  // Default to unlimited time (no limit)
    }
  });

  const resetForm = () => {
    setSelectedChildIds([]);
    setNewGoal({
      childId: 0,
      title: '',
      type: GoalType.HeartStreak,
      targetValue: 5,
      reward: '',
      dayPasses: 0,
      heartStreakTemplate: {
        consecutiveDays: 5,
        dayPasses: 1
      },
      starCollectionTemplate: {
        totalStars: 10,
        timePeriodDays: 7
      },
      heartCollectionTemplate: {
        totalHearts: 10,
        timePeriodDays: 30
      }
    });
  };

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- standard form-reset-on-close pattern; resetForm calls setState to clear fields when modal closes
      resetForm();
    }
  }, [open]);

  const generateGoalTitle = (): string => {
    if (newGoal.type === GoalType.HeartStreak) {
      const days = newGoal.heartStreakTemplate?.consecutiveDays || 5;
      const squares = newGoal.heartStreakTemplate?.dayPasses ?? 0;
      const passText = squares === 1 ? 'day pass' : 'day passes';
      return `Get hearts ${days} days in a row (${squares} ${passText})`;
    } else if (newGoal.type === GoalType.StarCollection) {
      const stars = newGoal.starCollectionTemplate?.totalStars || 10;
      const days = newGoal.starCollectionTemplate?.timePeriodDays || 0;
      
      // Issue #487: Only include time period if days > 0 (unlimited time is the default)
      // Issue #488: Remove squares from collection goal titles
      if (days > 0) {
        return `Collect ${stars} stars in ${days} days`;
      } else {
        return `Collect ${stars} stars`;
      }
    } else if (newGoal.type === GoalType.HeartCollection) {
      const hearts = newGoal.heartCollectionTemplate?.totalHearts || 10;
      const days = newGoal.heartCollectionTemplate?.timePeriodDays || 0;
      
      // Issue #487: Only include time period if days > 0 (unlimited time is the default)
      // Issue #488: Remove squares from collection goal titles
      if (days > 0) {
        return `Collect ${hearts} hearts in ${days} days`;
      } else {
        return `Collect ${hearts} hearts`;
      }
    }
    return '';
  };

  const handleSaveGoal = async () => {
    // Validation
    if (selectedChildIds.length === 0) {
      showSnackbar('Please select at least one child', 'error');
      return;
    }

    try {
      const generatedTitle = generateGoalTitle();
      
      const goalToSave = {
        childIds: selectedChildIds,
        title: generatedTitle,
        type: newGoal.type,
        targetValue: newGoal.targetValue,
        rewardDescription: newGoal.reward,
        dayPasses: newGoal.type === GoalType.StarCollection || newGoal.type === GoalType.HeartCollection ? 0 : newGoal.dayPasses,
        heartStreakTemplate: newGoal.heartStreakTemplate,
        starCollectionTemplate: newGoal.starCollectionTemplate,
        heartCollectionTemplate: newGoal.heartCollectionTemplate
      };

      // Use admin API endpoint
      await goalsAPI.adminCreateGoal(familyId, goalToSave);
      
      showSnackbar('Goal created successfully!', 'success');
      resetForm();
      onSave(); // Trigger parent refresh
      onClose();
    } catch (error) {
      console.error('Error creating goal:', error);
      showSnackbar('Failed to create goal. Please try again.', 'error');
    }
  };

  const handleGoalTypeChange = (newType: GoalType) => {
    let newTargetValue = newGoal.targetValue;
    
    // Auto-update targetValue to match template defaults
    if (newType === GoalType.HeartStreak) {
      newTargetValue = newGoal.heartStreakTemplate?.consecutiveDays || 5;
    } else if (newType === GoalType.StarCollection) {
      newTargetValue = newGoal.starCollectionTemplate?.totalStars || 10;
    } else if (newType === GoalType.HeartCollection) {
      newTargetValue = newGoal.heartCollectionTemplate?.totalHearts || 10;
    }
    
    setNewGoal({ ...newGoal, type: newType, targetValue: newTargetValue });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing on backdrop click
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>Create Goal</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* Child Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Select Children
            </Typography>
            <Box
              data-testid="child-selection-buttons"
              role="group"
              aria-label="Select children for goal"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mt: 1
              }}
            >
              {children.map((child) => {
                const childId = child.id;
                if (childId === undefined) return null;
                const isSelected = selectedChildIds.includes(childId);
                
                return (
                  <Button
                    key={childId}
                    data-testid={`child-button-${childId}`}
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => {
                      setSelectedChildIds(prev => 
                        isSelected 
                          ? prev.filter(id => id !== childId)
                          : [...prev, childId]
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedChildIds(prev => 
                          isSelected 
                            ? prev.filter(id => id !== childId)
                            : [...prev, childId]
                        );
                      }
                    }}
                    aria-pressed={isSelected}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 1
                    }}
                  >
                    {child.firstName}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* Goal Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Goal Type</InputLabel>
            <Select
              value={newGoal.type}
              onChange={(e) => handleGoalTypeChange(e.target.value as GoalType)}
              label="Goal Type"
            >
              <MenuItem value={GoalType.HeartStreak}>Heart Streak</MenuItem>
              <MenuItem value={GoalType.StarCollection}>Star Collection</MenuItem>
              <MenuItem value={GoalType.HeartCollection}>Heart Collection</MenuItem>
            </Select>
          </FormControl>

          {/* Dynamic Template Fields */}
          <Box data-testid="dynamic-fields">
            <Fade in={true} timeout={300}>
              <Box>
                {newGoal.type === GoalType.HeartStreak && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" color="primary">
                      Heart Streak Settings
                    </Typography>
                    <TextField
                      label="Consecutive Days"
                      type="number"
                      fullWidth
                      value={newGoal.heartStreakTemplate?.consecutiveDays || 5}
                      onChange={(e) => {
                        const days = Math.max(2, Math.min(30, parseInt(e.target.value) || 5));
                        setNewGoal({
                          ...newGoal,
                          targetValue: days,
                          heartStreakTemplate: {
                            ...newGoal.heartStreakTemplate!,
                            consecutiveDays: days
                          }
                        });
                      }}
                      inputProps={{ min: 2, max: 30 }}
                      helperText="2-30 days"
                    />
                    <TextField
                      label="Day Passes"
                      type="number"
                      fullWidth
                      value={newGoal.heartStreakTemplate?.dayPasses ?? 1}
                      onChange={(e) => {
                        const squares = Math.max(0, Math.min(3, parseInt(e.target.value) || 0));
                        setNewGoal({
                          ...newGoal,
                          heartStreakTemplate: {
                            ...newGoal.heartStreakTemplate!,
                            dayPasses: squares
                          }
                        });
                      }}
                      inputProps={{ min: 0, max: 3 }}
                      helperText="0-3 day passes (0 means no day passes)"
                    />
                  </Stack>
                )}

                {newGoal.type === GoalType.StarCollection && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" color="primary">
                      Star Collection Settings
                    </Typography>
                    <TextField
                      label="Total Stars"
                      type="number"
                      fullWidth
                      value={newGoal.starCollectionTemplate?.totalStars || 10}
                      onChange={(e) => {
                        const stars = Math.max(1, parseInt(e.target.value) || 10);
                        setNewGoal({
                          ...newGoal,
                          targetValue: stars,
                          starCollectionTemplate: {
                            ...newGoal.starCollectionTemplate!,
                            totalStars: stars
                          }
                        });
                      }}
                      inputProps={{ min: 1 }}
                      helperText="Number of stars to collect"
                    />
                    <TextField
                      label="Time Period (Days)"
                      type="number"
                      fullWidth
                      placeholder="No limit"
                      value={newGoal.starCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.starCollectionTemplate?.timePeriodDays ?? '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseInt(value);
                        
                        // Auto-clear to empty when user enters 0
                        if (numValue === 0) {
                          setNewGoal({
                            ...newGoal,
                            starCollectionTemplate: {
                              ...newGoal.starCollectionTemplate!,
                              timePeriodDays: 0
                            }
                          });
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                        
                        setNewGoal({
                          ...newGoal,
                          starCollectionTemplate: {
                            ...newGoal.starCollectionTemplate!,
                            timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                          }
                        });
                      }}
                      helperText="Days to collect the stars (empty = unlimited)"
                    />
                  </Stack>
                )}

                {newGoal.type === GoalType.HeartCollection && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" color="primary">
                      Heart Collection Settings
                    </Typography>
                    <TextField
                      label="Total Hearts"
                      type="number"
                      fullWidth
                      value={newGoal.heartCollectionTemplate?.totalHearts || 10}
                      onChange={(e) => {
                        const hearts = Math.max(1, parseInt(e.target.value) || 10);
                        setNewGoal({
                          ...newGoal,
                          targetValue: hearts,
                          heartCollectionTemplate: {
                            ...newGoal.heartCollectionTemplate!,
                            totalHearts: hearts
                          }
                        });
                      }}
                      inputProps={{ min: 1 }}
                      helperText="Number of hearts to collect"
                    />
                    <TextField
                      label="Time Period (Days)"
                      type="number"
                      fullWidth
                      placeholder="No limit"
                      value={newGoal.heartCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.heartCollectionTemplate?.timePeriodDays ?? '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseInt(value);
                        
                        // Auto-clear to empty when user enters 0
                        if (numValue === 0) {
                          setNewGoal({
                            ...newGoal,
                            heartCollectionTemplate: {
                              ...newGoal.heartCollectionTemplate!,
                              timePeriodDays: 0
                            }
                          });
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                        
                        setNewGoal({
                          ...newGoal,
                          heartCollectionTemplate: {
                            ...newGoal.heartCollectionTemplate!,
                            timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                          }
                        });
                      }}
                      helperText="Days to collect the hearts (empty = unlimited)"
                    />
                  </Stack>
                )}
              </Box>
            </Fade>
          </Box>

          {/* Reward Description */}
          <TextField
            label="Reward Description"
            fullWidth
            multiline
            rows={3}
            value={newGoal.reward}
            onChange={(e) => setNewGoal({ ...newGoal, reward: e.target.value })}
            placeholder="What will the child receive for completing this goal?"
            helperText="Describe the reward the child will earn"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveGoal}
          startIcon={<AddIcon />}
        >
          Create Goal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminGoalCreationModal;